/**
 * The image pipeline's browser half: decode, shrink, crop and re-encode.
 * It runs inside a Web Worker on OffscreenCanvas where the browser has
 * them (image.worker.ts), so the UI never stalls, and on the main thread
 * otherwise (index.ts decides). Nothing here touches the DOM except
 * through `makeCanvas`, which uses a <canvas> element only when there is
 * no OffscreenCanvas.
 *
 * Big sources never need a full-size canvas: createImageBitmap decodes
 * and shrinks in one step (resizeWidth/resizeHeight, resizeQuality
 * 'high'), honouring EXIF orientation ('from-image'). Where a browser
 * ignores the resize options, a stepped downscale (halving) takes over.
 * Reductions of 2x and more after that (an avatar crop of 2048 px down to
 * 512) go through a Lanczos-3 filter (resample.ts), not the canvas's own
 * smoothing, which turns fine patterns into moiré.
 *
 * ORIENTATION: the editor's bitmap is redrawn onto a plain canvas once it
 * is decoded (`prepare`), so it holds upright pixels with no orientation
 * of its own. Chrome crops a bitmap decoded with imageOrientation
 * 'from-image' in the wrong (unrotated) frame when createImageBitmap is
 * given a source rectangle: a portrait phone photo (EXIF orientation 6 or
 * 8) panned to one corner came out as the opposite corner. Crops are made
 * with drawImage, which gets it right.
 *
 * PRIVACY: re-encoding draws only pixels onto a fresh canvas, so the file
 * written carries no EXIF, XMP or IPTC at all: no camera, no time, no GPS
 * location. That is deliberate. (The server strips metadata again.)
 */
import {
  animationPlan,
  capCanvas,
  decodeSize,
  encodeAttempts,
  encodeToFit,
  ImageTooLargeError,
  MAX_CANVAS_AREA,
  PRESETS,
  presetFor,
  wantsPng,
  workingSize,
  type AnimationPlan,
  type EncodeAttempt,
  type EncodeType,
  type Preset,
  type Size,
} from './plan';
import type { CropRect } from './crop';
import { resampleRGBA } from './resample';
import { orientedSize, sniffSource, type SourceInfo } from './sniff';
import type { ImagePurpose } from '@neosocial/protocol';

export type ImageErrorKind = 'heic' | 'unsupported' | 'decode' | 'tooLarge';

/** Something the user can act on: an unsupported format, a damaged file. */
export class ImageError extends Error {
  readonly kind: ImageErrorKind;
  constructor(kind: ImageErrorKind, message: string = kind) {
    super(message);
    this.name = 'ImageError';
    this.kind = kind;
  }
}

export type Progress = (fraction: number) => void;

type AnyCanvas = OffscreenCanvas | HTMLCanvasElement;
type Ctx2D = OffscreenCanvasRenderingContext2D | CanvasRenderingContext2D;

export function makeCanvas(w: number, h: number): AnyCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  return c;
}

function ctx2d(c: AnyCanvas, alpha = true): Ctx2D {
  const ctx = c.getContext('2d', { alpha }) as Ctx2D | null;
  if (!ctx) throw new ImageError('decode', 'no 2d context');
  return ctx;
}

export function canvasToBlob(c: AnyCanvas, type: EncodeType, quality?: number): Promise<Blob> {
  if ('convertToBlob' in c) return c.convertToBlob({ type, quality });
  return new Promise((resolve, reject) =>
    c.toBlob(
      (b) => (b ? resolve(b) : reject(new ImageError('decode', 'encode failed'))),
      type,
      quality,
    ),
  );
}

let webpOk: Promise<boolean> | null = null;
/** Whether this browser can write WebP (Safari before 17 writes PNG when asked). */
export function supportsWebp(): Promise<boolean> {
  webpOk ??= (async () => {
    // A canvas needs a context (and some pixels) before it will encode.
    const c = makeCanvas(2, 2);
    const g = ctx2d(c);
    g.fillStyle = '#808080';
    g.fillRect(0, 0, 2, 2);
    return (await canvasToBlob(c, 'image/webp', 0.8)).type === 'image/webp';
  })().catch(() => false);
  return webpOk;
}

/** Animations up to this size are read whole, to count every frame. */
const FULL_READ = 16 * 1024 * 1024;

/** Reads the header (the first MB); a GIF or WebP up to 16 MB whole, to count its frames. */
export async function readInfo(file: Blob): Promise<SourceInfo> {
  const head = new Uint8Array(await file.slice(0, 1 << 20).arrayBuffer());
  let info = sniffSource(head);
  if (
    (info.kind === 'gif' || info.kind === 'webp') &&
    file.size > head.length &&
    file.size <= FULL_READ
  )
    info = sniffSource(new Uint8Array(await file.arrayBuffer()));
  return info;
}

const ORIENT: ImageBitmapOptions = { imageOrientation: 'from-image' };

/**
 * Decodes `file` shrunk to fit `want(upright size)`, without a full-size
 * canvas. The size is passed as one dimension only, so the browser keeps
 * the aspect ratio of the upright image even if our reading of the EXIF
 * orientation were wrong; the result is then checked and corrected.
 */
export async function decodeScaled(
  file: Blob,
  info: SourceInfo,
  want: (src: Size) => Size,
): Promise<ImageBitmap> {
  const fail = (e: unknown): never => {
    if (info.kind === 'heic') throw new ImageError('heic');
    if (info.kind === 'unknown') throw new ImageError('unsupported');
    throw new ImageError('decode', e instanceof Error ? e.message : 'decode failed');
  };
  const src = orientedSize(info);
  let bmp: ImageBitmap;
  if (src) {
    const t = want(src);
    const opts: ImageBitmapOptions =
      t.width >= src.width && t.height >= src.height
        ? ORIENT
        : { ...ORIENT, resizeWidth: t.width, resizeQuality: 'high' };
    bmp = await createImageBitmap(file, opts).catch(fail);
  } else {
    // HEIC, AVIF, BMP: no size from the header; decode, then shrink the bitmap.
    bmp = await createImageBitmap(file, ORIENT).catch(fail);
  }
  return fitBitmap(bmp, want({ width: bmp.width, height: bmp.height }));
}

/**
 * Makes sure `bmp` is no bigger than `t` (some browsers ignore the resize
 * options). Drawn, not resized with createImageBitmap: that resize aliases
 * badly, and drawImage handles an oriented bitmap correctly.
 */
async function fitBitmap(bmp: ImageBitmap, t: Size): Promise<ImageBitmap> {
  if (bmp.width <= t.width + 1 && bmp.height <= t.height + 1) return bmp;
  const c = drawScaled(bmp, null, t);
  bmp.close();
  return toBitmap(c);
}

/**
 * Redraws `bmp` onto a plain canvas: the copy holds upright pixels and no
 * orientation of its own, so later crops of it are plain (see ORIENTATION
 * above). `bmp` is closed.
 */
export async function bakeOrientation(bmp: ImageBitmap): Promise<ImageBitmap> {
  const c = makeCanvas(bmp.width, bmp.height);
  ctx2d(c).drawImage(bmp, 0, 0);
  bmp.close();
  return toBitmap(c);
}

function toBitmap(c: AnyCanvas): Promise<ImageBitmap> {
  if ('transferToImageBitmap' in c) return Promise.resolve(c.transferToImageBitmap());
  return createImageBitmap(c);
}

/**
 * Draws `rect` of `src` (all of it when null) at `out` size, halving in
 * steps while the reduction is more than 2x, so a big reduction stays sharp
 * and no step's canvas is bigger than half the previous one.
 */
export function drawScaled(
  src: CanvasImageSource & Size,
  rect: CropRect | null,
  out: Size,
): AnyCanvas {
  let r = rect ?? { sx: 0, sy: 0, sw: src.width, sh: src.height };
  if (Math.min(r.sw / out.width, r.sh / out.height) >= 2 && r.sw * r.sh <= MAX_CANVAS_AREA) {
    try {
      return drawFiltered(src, r, out);
    } catch {
      /* no pixel access here: the canvas's smoothing below */
    }
  }
  let cur: CanvasImageSource = src;
  let tmp: AnyCanvas | null = null;
  while (r.sw / out.width > 2 && r.sh / out.height > 2) {
    const next = capCanvas({ width: Math.ceil(r.sw / 2), height: Math.ceil(r.sh / 2) });
    const c = makeCanvas(next.width, next.height);
    const g = ctx2d(c);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.drawImage(cur, r.sx, r.sy, r.sw, r.sh, 0, 0, next.width, next.height);
    if (tmp) shrink(tmp);
    tmp = c;
    cur = c;
    r = { sx: 0, sy: 0, sw: next.width, sh: next.height };
  }
  const c = makeCanvas(out.width, out.height);
  const g = ctx2d(c);
  g.imageSmoothingEnabled = true;
  g.imageSmoothingQuality = 'high';
  g.drawImage(cur, r.sx, r.sy, r.sw, r.sh, 0, 0, out.width, out.height);
  if (tmp) shrink(tmp);
  return c;
}

/**
 * Draws `rect` of `src` at `out` size through the Lanczos filter: the
 * crop at its own size first (drawImage, 1:1), then resampled in JS.
 */
function drawFiltered(src: CanvasImageSource & Size, r: CropRect, out: Size): AnyCanvas {
  const w = Math.max(1, Math.round(r.sw));
  const h = Math.max(1, Math.round(r.sh));
  const full = makeCanvas(w, h);
  try {
    const g = ctx2d(full);
    g.imageSmoothingEnabled = true;
    g.imageSmoothingQuality = 'high';
    g.drawImage(src, r.sx, r.sy, r.sw, r.sh, 0, 0, w, h);
    const px = resampleRGBA(g.getImageData(0, 0, w, h).data, w, h, out.width, out.height);
    const c = makeCanvas(out.width, out.height);
    ctx2d(c).putImageData(new ImageData(px, out.width, out.height), 0, 0);
    return c;
  } finally {
    shrink(full);
  }
}

/** Frees a canvas's memory now rather than at garbage collection. */
function shrink(c: AnyCanvas): void {
  c.width = 1;
  c.height = 1;
}

/** Transparency, and few colours (a logo, a UI screenshot), from a 64x64 nearest-pixel sample. */
export function sampleTraits(
  src: CanvasImageSource & Size,
  rect: CropRect | null,
): { alpha: boolean; flat: boolean } {
  const n = 64;
  const c = makeCanvas(n, n);
  const g = ctx2d(c);
  g.imageSmoothingEnabled = false;
  const r = rect ?? { sx: 0, sy: 0, sw: src.width, sh: src.height };
  g.clearRect(0, 0, n, n);
  g.drawImage(src, r.sx, r.sy, r.sw, r.sh, 0, 0, n, n);
  const d = g.getImageData(0, 0, n, n).data;
  let alpha = false;
  const colours = new Set<number>();
  for (let i = 0; i < d.length; i += 4) {
    if (d[i + 3]! < 250) alpha = true;
    if (colours.size <= 64) colours.add((d[i]! << 16) | (d[i + 1]! << 8) | d[i + 2]!);
  }
  shrink(c);
  return { alpha, flat: colours.size <= 64 };
}

export interface EncodedImage {
  blob: Blob;
  width: number;
  height: number;
  type: string;
}

/**
 * Encodes `src` (cropped to `rect`) for `preset`: WebP keeping alpha, or
 * JPEG / PNG where WebP cannot be written, PNG also tried for tiny and
 * flat-colour images; quality steps down, then the dimensions, until the
 * file fits the target.
 */
export async function encodeImage(
  src: CanvasImageSource & Size,
  preset: Preset,
  rect: CropRect | null,
  onProgress: Progress = () => {},
  signal?: AbortSignal,
): Promise<EncodedImage> {
  const area: Size = rect
    ? { width: Math.round(rect.sw), height: Math.round(rect.sh) }
    : { width: src.width, height: src.height };
  const webp = await supportsWebp();
  const traits = sampleTraits(src, rect);
  const attempts = encodeAttempts(preset, area, { webp, alpha: traits.alpha });
  const first = attempts[0]!;
  const png: EncodeAttempt | undefined =
    first.type !== 'image/png' && wantsPng(first, traits)
      ? { width: first.width, height: first.height, type: 'image/png' }
      : undefined;
  // One canvas per size, reused across quality steps.
  let drawn: { size: string; canvas: AnyCanvas } | null = null;
  const encode = async (a: EncodeAttempt) => {
    const key = `${a.width}x${a.height}`;
    if (drawn?.size !== key) {
      if (drawn) shrink(drawn.canvas);
      // (JPEG is only chosen for opaque images: transparency without WebP goes PNG.)
      drawn = { size: key, canvas: drawScaled(src, rect, a) };
    }
    return canvasToBlob(drawn.canvas, a.type, a.quality);
  };
  try {
    const r = await encodeToFit(attempts, encode, preset, {
      png,
      signal,
      onStep: (done, total) => onProgress(Math.min(1, done / Math.min(total, 6))),
    });
    return { blob: r.blob, width: r.attempt.width, height: r.attempt.height, type: r.blob.type };
  } catch (e) {
    if (e instanceof ImageTooLargeError) throw new ImageError('tooLarge');
    throw e;
  } finally {
    if (drawn) shrink((drawn as { canvas: AnyCanvas }).canvas);
  }
}

/* ------------------------------------------------------------ the jobs */

export interface OptimizeResult extends EncodedImage {
  /** Sent as it was (a small animation). */
  passthrough: boolean;
  /** Set when an animation became a still, so the UI can say so. */
  still: Extract<AnimationPlan, { kind: 'still' }>['reason'] | null;
  /** The source's upright size, when known. */
  sourceWidth: number | null;
  sourceHeight: number | null;
}

/**
 * An image for a purpose without the editor (chat images, emoji): decode
 * shrunk, encode to fit. `uploadImageBytes` is the console's chat-image
 * upload limit, when the client knows it (plan.ts `presetFor`).
 */
export async function optimize(
  file: Blob,
  purpose: ImagePurpose,
  onProgress: Progress = () => {},
  signal?: AbortSignal,
  uploadImageBytes?: number,
): Promise<OptimizeResult> {
  const preset = presetFor(purpose, uploadImageBytes);
  const info = await readInfo(file);
  onProgress(0.08);
  const plan = animationPlan(preset, info, file.size);
  const up = orientedSize(info);
  const base = { sourceWidth: up?.width ?? null, sourceHeight: up?.height ?? null };
  if (plan.kind === 'passthrough') {
    onProgress(1);
    return {
      blob: file,
      width: info.width ?? 0,
      height: info.height ?? 0,
      type: info.kind === 'gif' ? 'image/gif' : 'image/webp',
      passthrough: true,
      still: null,
      ...base,
    };
  }
  const bmp = await decodeScaled(file, info, (s) => decodeSize(preset, s));
  try {
    onProgress(0.55);
    const out = await encodeImage(bmp, preset, null, (p) => onProgress(0.55 + p * 0.45), signal);
    onProgress(1);
    return {
      ...out,
      passthrough: false,
      still: plan.kind === 'still' ? plan.reason : null,
      sourceWidth: base.sourceWidth ?? bmp.width,
      sourceHeight: base.sourceHeight ?? bmp.height,
    };
  } finally {
    bmp.close();
  }
}

export interface PreparedImage {
  /** Upright (baked: no orientation of its own), shrunk to what the editor's highest zoom needs. */
  bitmap: ImageBitmap;
  still: Extract<AnimationPlan, { kind: 'still' }>['reason'] | null;
}

/** Decodes an image for the crop editor (avatars, icons, banners). */
export async function prepare(
  file: Blob,
  purpose: ImagePurpose,
  onProgress: Progress = () => {},
): Promise<PreparedImage> {
  const preset = PRESETS[purpose];
  const info = await readInfo(file);
  onProgress(0.15);
  const plan = animationPlan(preset, info, file.size);
  const decoded = await decodeScaled(file, info, (s) => workingSize(preset, s));
  onProgress(0.85);
  const bitmap = await bakeOrientation(decoded);
  onProgress(1);
  return { bitmap, still: plan.kind === 'still' ? plan.reason : null };
}

/** Encodes the editor's crop of a prepared bitmap. */
export function encodeCrop(
  bitmap: ImageBitmap,
  purpose: ImagePurpose,
  rect: CropRect,
  onProgress: Progress = () => {},
): Promise<EncodedImage> {
  return encodeImage(bitmap, PRESETS[purpose], rect, onProgress);
}
